import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, DragOverEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { useDragDrop } from '@/hooks/useDragDrop';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { Canvas } from './Canvas';
import { DraggableChord } from './DraggableChord';
import { ChordShape } from './ChordShape';
import { SelectionBox } from './SelectionBox';
import { ChordPalette } from '@/components/Palette/ChordPalette';
import { SuspensionPalette } from './SuspensionPalette';
import { ChordDropZone } from './ChordDropZone';
import { SuspensionConnectorShape } from './SuspensionConnectorShape';
import { SuspensionContextMenu } from './SuspensionContextMenu';
import { SuggestionsLayer } from './SuggestionsLayer';
import type { GhostChordPayload } from '@/lib/ghosts';
import { useCanvasStore } from '@/store/canvas-store';
import { validateSuspensionPlacement } from '@/lib/suspension-placement';
import { SNIPPET_DRAG_MIME, beatFromDropX } from '@/utils/snippet-insert';
import type { Chord, ScaleDegree, MusicalKey, Mode } from '@/types';
import type { PhraseBoundary } from '@/types/progression';
import type { SongContext } from '@/store/why-this-store';
import type { SuspensionType, SuspensionDragData, ChordDropZoneData } from '@/types/suspension-connector';
import { CANVAS_CONFIG } from '@/utils/constants';
import styles from './DroppableCanvas.module.css';

// Handwriting status line reads as a sentence, not a counter — spell out
// small counts (foundation §7: lowercase, concrete, kind).
const COUNT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
function describeSelectionCount(count: number): string {
  return COUNT_WORDS[count] ?? String(count);
}

interface DroppableCanvasProps {
  chords: Chord[];
  phrases?: PhraseBoundary[];
  songContext?: SongContext;
  currentKey: MusicalKey;
  currentMode: Mode;
  beatsPerMeasure?: number;
  zoom?: number;
  isPlaying?: boolean;
  playheadPosition?: number;
  totalBeats?: number;
  selectedChordIds: string[];
  showVoiceLanes?: boolean;
  onUpdateChordPosition: (chordId: string, startBeat: number) => void;
  onUpdateChord: (chordId: string, updates: Partial<Chord>) => void;
  onAddChord: (scaleDegree: ScaleDegree, position: { x: number }, options?: { quality?: import('@/types').ChordQuality; extensions?: import('@/types').ChordExtensions }) => void;
  onSelectChord: (id: string) => void;
  onSelectChords: (ids: string[]) => void;
  onToggleChordSelection: (id: string) => void;
  onSelectRange: (fromId: string, toId: string) => void;
  onClearSelection: () => void;
  onSelectAll: () => void;
  onDeleteSelected?: () => void;
  onDuplicateSelected?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onZoomChange?: (zoom: number) => void;
  onShowHelp?: () => void;
  onTogglePlay?: () => void;
  onStop?: () => void;
  onTempoChange?: (delta: number) => void;
  onSave?: () => void;
  onAnalyze?: () => void;
  onRefine?: () => void;
  onDeepInsight?: () => void;
  onBuildFromBones?: () => void;
  /** Called when user seeks to a position (GRE-115) */
  onSeek?: (beat: number) => void;
  /** Play a chord isolated (GRE-117) */
  onPlayChord?: (chord: Chord) => void;
  /** GRE-115: Seek to start of timeline */
  onSeekToStart?: () => void;
  /** GRE-115: Seek to end of timeline */
  onSeekToEnd?: () => void;
  /** Ghost audition (hover): voice + play once, no commit. */
  onAuditionGhost?: (payload: GhostChordPayload) => void;
  /** Ghost commit (click): append the candidate at the next beat. */
  onCommitGhost?: (payload: GhostChordPayload) => void;
  /** Drop a snippet from SnippetsPanel at the given beat (native HTML5 DnD — see snippet-insert.ts). */
  onInsertSnippet?: (snippetId: string, dropBeat: number) => void;
}

export const DroppableCanvas: React.FC<DroppableCanvasProps> = ({
  chords,
  phrases = [],
  songContext,
  currentKey,
  currentMode,
  beatsPerMeasure = 4,
  zoom = 1.0,
  isPlaying = false,
  playheadPosition = 0,
  totalBeats = 32,
  selectedChordIds,
  showVoiceLanes = false,
  onUpdateChordPosition,
  onUpdateChord,
  onAddChord,
  onSelectChord,
  onSelectChords,
  onToggleChordSelection,
  onSelectRange,
  onClearSelection,
  onSelectAll,
  onDeleteSelected,
  onDuplicateSelected,
  onUndo,
  onRedo,
  onZoomChange,
  onShowHelp,
  onTogglePlay,
  onStop,
  onTempoChange,
  onSave,
  onAnalyze,
  onRefine,
  onDeepInsight,
  onBuildFromBones,
  onSeek,
  onPlayChord,
  onSeekToStart,
  onSeekToEnd,
  onAuditionGhost,
  onCommitGhost,
  onInsertSnippet,
}) => {
  const { sensors } = useDragDrop();
  const [activeChord, setActiveChord] = useState<Chord | null>(null);
  const [activePaletteItem, setActivePaletteItem] = useState<{ scaleDegree: ScaleDegree } | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const canvasScrollRef = useRef<HTMLDivElement>(null);

  // Suspension connector state (GRE-113)
  const [suspensionPaletteVisible, setSuspensionPaletteVisible] = useState(false);
  const [activeSuspensionType, setActiveSuspensionType] = useState<SuspensionType | null>(null);
  const [hoveredDropZoneId, setHoveredDropZoneId] = useState<string | null>(null);

  // Suspension store (GRE-113)
  const suspensionConnectors = useCanvasStore((state) => state.suspensionConnectors);
  const selectedConnectorId = useCanvasStore((state) => state.selectedConnectorId);
  const isDraggingSuspension = useCanvasStore((state) => state.isDraggingSuspension);
  const addSuspensionConnector = useCanvasStore((state) => state.addSuspensionConnector);
  const removeSuspensionConnector = useCanvasStore((state) => state.removeSuspensionConnector);
  const updateSuspensionConnector = useCanvasStore((state) => state.updateSuspensionConnector);
  const selectConnector = useCanvasStore((state) => state.selectConnector);
  const setDraggingSuspension = useCanvasStore((state) => state.setDraggingSuspension);

  // Suspension context menu state (GRE-113)
  const [suspensionContextMenu, setSuspensionContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    connectorId: string | null;
  }>({ isOpen: false, position: { x: 0, y: 0 }, connectorId: null });

  // Ref to track current chords - avoids stale closure in drag handlers
  const chordsRef = useRef(chords);
  useEffect(() => {
    chordsRef.current = chords;
  }, [chords]);

  // Selection box state
  const [selectionBox, setSelectionBox] = useState<{
    isSelecting: boolean;
    start: { x: number; y: number };
    current: { x: number; y: number };
  } | null>(null);

  const beatWidth = CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom;

  // Compute drop zones between adjacent chords (GRE-113)
  const dropZones = useMemo((): ChordDropZoneData[] => {
    if (chords.length < 2) return [];

    // Sort chords by position
    const sortedChords = [...chords].sort((a, b) => a.startBeat - b.startBeat);
    const zones: ChordDropZoneData[] = [];

    for (let i = 0; i < sortedChords.length - 1; i++) {
      const sourceChord = sortedChords[i];
      const targetChord = sortedChords[i + 1];

      // Validate if a suspension can be placed here (if we're dragging one)
      let isValid = true;
      let invalidReason: string | undefined;

      if (activeSuspensionType) {
        const validation = validateSuspensionPlacement(
          sourceChord,
          targetChord,
          activeSuspensionType
        );
        isValid = validation.valid;
        invalidReason = validation.reason;
      }

      zones.push({
        id: `dropzone-${sourceChord.id}-${targetChord.id}`,
        sourceChordId: sourceChord.id,
        targetChordId: targetChord.id,
        isValid,
        invalidReason,
      });
    }

    return zones;
  }, [chords, activeSuspensionType]);

  // Calculate chord positions for rendering connectors (GRE-113)
  const getChordPosition = useCallback((chordId: string): { x: number; y: number } => {
    const chord = chords.find((c) => c.id === chordId);
    if (!chord) return { x: 0, y: 0 };

    const x = chord.startBeat * beatWidth + (chord.size * zoom) / 2;
    const y = 60 * zoom; // Approximate vertical center of chord area
    return { x, y };
  }, [chords, beatWidth, zoom]);

  // Handle arrow key movement
  const handleMoveSelected = useCallback((direction: 'left' | 'right', amount: number) => {
    if (selectedChordIds.length === 0) return;

    const delta = direction === 'left' ? -amount : amount;
    selectedChordIds.forEach(id => {
      const chord = chords.find(c => c.id === id);
      if (chord) {
        const newBeat = Math.max(0, chord.startBeat + delta);
        onUpdateChordPosition(id, newBeat);
      }
    });
  }, [selectedChordIds, chords, onUpdateChordPosition]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSelectAll,
    onClearSelection,
    onDeleteSelected,
    onDuplicateSelected,
    onUndo,
    onRedo,
    onMoveSelected: handleMoveSelected,
    onTempoChange,
    onShowHelp,
    onTogglePlay,
    onStop,
    onSave,
    onSeekToStart,
    onSeekToEnd,
  });

  // Handle chord click for selection
  const handleChordClick = useCallback((chord: Chord, e: React.MouseEvent) => {
    e.stopPropagation();

    if (e.metaKey || e.ctrlKey) {
      onToggleChordSelection(chord.id);
    } else if (e.shiftKey) {
      const lastSelected = selectedChordIds[selectedChordIds.length - 1];
      if (lastSelected) {
        onSelectRange(lastSelected, chord.id);
      } else {
        onSelectChord(chord.id);
      }
    } else {
      onSelectChord(chord.id);
    }
  }, [selectedChordIds, onSelectChord, onToggleChordSelection, onSelectRange]);

  // Clear selection when clicking empty space
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('[data-chord]')) {
      onClearSelection();
    }
  }, [onClearSelection]);

  // Selection box handlers
  const handleSelectionStart = useCallback((e: React.MouseEvent) => {
    // Only start selection if Shift is held and clicking empty space (not a chord)
    if (!e.shiftKey) return;

    const target = e.target as HTMLElement;
    if (target.closest('[data-chord]')) return;

    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setSelectionBox({
      isSelecting: true,
      start: { x, y },
      current: { x, y },
    });
  }, []);

  const handleSelectionMove = useCallback((e: React.MouseEvent) => {
    if (!selectionBox?.isSelecting) return;

    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setSelectionBox(prev => prev ? { ...prev, current: { x, y } } : null);
  }, [selectionBox?.isSelecting]);

  const handleSelectionEnd = useCallback(() => {
    if (!selectionBox?.isSelecting) return;

    // Calculate selection bounds
    const minX = Math.min(selectionBox.start.x, selectionBox.current.x);
    const maxX = Math.max(selectionBox.start.x, selectionBox.current.x);
    const minY = Math.min(selectionBox.start.y, selectionBox.current.y);
    const maxY = Math.max(selectionBox.start.y, selectionBox.current.y);

    // Find chords within the selection box
    const selectedIds = chords.filter(chord => {
      const chordX = chord.startBeat * beatWidth;
      const chordSize = chord.size * zoom;
      const chordCenterX = chordX + chordSize / 2;
      const chordCenterY = 40 * zoom; // Approximate center Y in chord area

      return chordCenterX >= minX && chordCenterX <= maxX &&
             chordCenterY >= minY - 100 && chordCenterY <= maxY + 100; // More forgiving Y bounds
    }).map(c => c.id);

    if (selectedIds.length > 0) {
      onSelectChords(selectedIds);
    }

    setSelectionBox(null);
  }, [selectionBox, chords, beatWidth, zoom, onSelectChords]);

  // Drag handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current;

    // Check if this is a suspension connector drag (GRE-113)
    if (data?.type === 'suspension-connector') {
      const suspensionData = data as SuspensionDragData;
      setActiveSuspensionType(suspensionData.suspensionType);
      setDraggingSuspension(true);
      setActiveChord(null);
      setActivePaletteItem(null);
      return;
    }

    // Check if this is a palette chord drag
    if (data?.type === 'palette-chord') {
      setActivePaletteItem({ scaleDegree: data.scaleDegree as ScaleDegree });
      setActiveChord(null);
      setActiveSuspensionType(null);
      return;
    }

    // Otherwise it's an existing chord
    const chord = chords.find(c => c.id === active.id);
    if (chord) {
      setActiveChord(chord);
      setActivePaletteItem(null);
      setActiveSuspensionType(null);
    }
  }, [chords, setDraggingSuspension]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, delta, over } = event;
    const data = active.data.current;

    // Handle suspension connector drops (GRE-113)
    if (data?.type === 'suspension-connector' && activeSuspensionType) {
      // Check if dropped on a valid drop zone
      if (over && over.data.current?.type === 'chord-drop-zone') {
        const dropZoneData = over.data.current as {
          sourceChordId: string;
          targetChordId: string;
          isValid: boolean;
          invalidReason?: string;
        };

        if (dropZoneData.isValid) {
          // Find the source and target chords
          const sourceChord = chords.find((c) => c.id === dropZoneData.sourceChordId);
          const targetChord = chords.find((c) => c.id === dropZoneData.targetChordId);

          if (sourceChord && targetChord) {
            // Validate and get placement details
            const validation = validateSuspensionPlacement(
              sourceChord,
              targetChord,
              activeSuspensionType
            );

            if (validation.valid && validation.placement) {
              // Add the suspension connector
              addSuspensionConnector({
                suspensionType: activeSuspensionType,
                sourceChordId: dropZoneData.sourceChordId,
                targetChordId: dropZoneData.targetChordId,
                suspendedVoice: validation.placement.suspendedVoice,
                preparationNote: validation.placement.preparationNote,
                suspensionNote: validation.placement.suspensionNote,
                resolutionNote: validation.placement.resolutionNote,
                suspensionDuration: 2, // Default 2 beats
              });
            }
          }
        }
      }

      // Clean up suspension drag state
      setActiveSuspensionType(null);
      setDraggingSuspension(false);
      setHoveredDropZoneId(null);
      return;
    }

    // Handle palette chord drops
    if (data?.type === 'palette-chord') {
      const scaleDegree = data.scaleDegree as ScaleDegree;

      // Calculate drop position from initial pointer + delta
      const pointerEvent = event.activatorEvent as PointerEvent | MouseEvent | TouchEvent;
      let startX = 0;

      if (pointerEvent && 'clientX' in pointerEvent) {
        startX = pointerEvent.clientX;
      } else if (pointerEvent && 'touches' in pointerEvent && pointerEvent.touches[0]) {
        startX = pointerEvent.touches[0].clientX;
      }

      if (canvasContainerRef.current && (startX > 0 || delta)) {
        const canvasRect = canvasContainerRef.current.getBoundingClientRect();
        const scrollLeft = canvasScrollRef.current?.scrollLeft || 0;

        // Final X position = start position + delta, relative to canvas
        const finalX = startX + (delta?.x || 0);
        const dropX = finalX - canvasRect.left + scrollLeft;

        // Snap to beat - subtract half beat width to center the drop
        const startBeat = Math.max(0, Math.floor((dropX - beatWidth / 2) / beatWidth));

        // Add the chord
        onAddChord(scaleDegree, { x: startBeat * beatWidth });
      } else {
        // Fallback: add at end of progression
        const lastChord = chords.length > 0
          ? chords.reduce((prev, curr) => curr.startBeat > prev.startBeat ? curr : prev)
          : null;
        const nextBeat = lastChord ? lastChord.startBeat + lastChord.duration : 0;
        onAddChord(scaleDegree, { x: nextBeat * beatWidth });
      }

      setActivePaletteItem(null);
      setActiveChord(null);
      return;
    }

    // Handle existing chord movement
    if (!delta) {
      setActiveChord(null);
      setActivePaletteItem(null);
      return;
    }

    const isSelected = selectedChordIds.includes(active.id as string);
    const chordsToMove = isSelected && selectedChordIds.length > 1
      ? selectedChordIds
      : [active.id as string];

    // Use ref to get current chords (avoids stale closure from @dnd-kit)
    const currentChords = chordsRef.current;

    // Move chords horizontally
    chordsToMove.forEach((id) => {
      const chord = currentChords.find(c => c.id === id);
      if (!chord) return;

      // Calculate new X position and snap to beat
      const currentX = chord.startBeat * beatWidth;
      const newX = currentX + delta.x;
      const snappedBeat = Math.max(0, Math.round(newX / beatWidth));

      onUpdateChordPosition(id, snappedBeat);
    });

    setActiveChord(null);
    setActivePaletteItem(null);
    setActiveSuspensionType(null);
    setDraggingSuspension(false);
  }, [beatWidth, chords, selectedChordIds, onUpdateChordPosition, onAddChord, activeSuspensionType, addSuspensionConnector, setDraggingSuspension]);

  const handleDragCancel = useCallback(() => {
    setActiveChord(null);
    setActivePaletteItem(null);
    setActiveSuspensionType(null);
    setDraggingSuspension(false);
    setHoveredDropZoneId(null);
  }, [setDraggingSuspension]);

  // Handle drag over for drop zone hover state (GRE-113)
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    if (over && over.data.current?.type === 'chord-drop-zone') {
      setHoveredDropZoneId(over.id as string);
    } else {
      setHoveredDropZoneId(null);
    }
  }, []);

  // Handle connector selection (GRE-113)
  const handleConnectorClick = useCallback((connectorId: string) => {
    selectConnector(selectedConnectorId === connectorId ? null : connectorId);
  }, [selectConnector, selectedConnectorId]);

  // Handle connector context menu (GRE-113)
  const handleConnectorContextMenu = useCallback((e: React.MouseEvent, connectorId: string) => {
    e.preventDefault();
    e.stopPropagation();
    selectConnector(connectorId);
    setSuspensionContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      connectorId,
    });
  }, [selectConnector]);

  // Close suspension context menu
  const closeSuspensionContextMenu = useCallback(() => {
    setSuspensionContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Handle suspension delete from context menu
  const handleSuspensionDelete = useCallback((id: string) => {
    removeSuspensionConnector(id);
    closeSuspensionContextMenu();
  }, [removeSuspensionConnector, closeSuspensionContextMenu]);

  // Handle suspension type change from context menu
  const handleSuspensionTypeChange = useCallback((id: string, newType: SuspensionType) => {
    // When changing type, we need to recalculate the notes
    const connector = suspensionConnectors.find((c) => c.id === id);
    if (!connector) return;

    const sourceChord = chords.find((c) => c.id === connector.sourceChordId);
    const targetChord = chords.find((c) => c.id === connector.targetChordId);

    if (sourceChord && targetChord) {
      const validation = validateSuspensionPlacement(sourceChord, targetChord, newType);
      if (validation.valid && validation.placement) {
        updateSuspensionConnector(id, {
          suspensionType: newType,
          suspendedVoice: validation.placement.suspendedVoice,
          preparationNote: validation.placement.preparationNote,
          suspensionNote: validation.placement.suspensionNote,
          resolutionNote: validation.placement.resolutionNote,
        });
      }
    }
    closeSuspensionContextMenu();
  }, [suspensionConnectors, chords, updateSuspensionConnector, closeSuspensionContextMenu]);

  // Handle suspension duration change from context menu
  const handleSuspensionDurationChange = useCallback((id: string, newDuration: number) => {
    updateSuspensionConnector(id, { suspensionDuration: newDuration });
    closeSuspensionContextMenu();
  }, [updateSuspensionConnector, closeSuspensionContextMenu]);

  // Native HTML5 drag-and-drop for snippets dragged in from SnippetsPanel
  // (which lives outside this component's @dnd-kit DndContext, so it can't
  // participate in the palette-chord/existing-chord drag session above).
  const handleSnippetDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes(SNIPPET_DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleSnippetDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const snippetId = e.dataTransfer.getData(SNIPPET_DRAG_MIME);
    if (!snippetId || !onInsertSnippet) return;
    e.preventDefault();

    const canvasRect = canvasContainerRef.current?.getBoundingClientRect();
    const scrollLeft = canvasScrollRef.current?.scrollLeft || 0;
    const dropX = canvasRect ? e.clientX - canvasRect.left + scrollLeft : 0;
    const dropBeat = beatFromDropX(dropX, zoom);

    onInsertSnippet(snippetId, dropBeat);
  }, [onInsertSnippet, zoom]);

  // Handle adding chord from palette click (non-drag)
  const handlePaletteAddChord = useCallback((scaleDegree: ScaleDegree) => {
    // Find the next available position (after the last chord)
    const lastChord = chords.length > 0
      ? chords.reduce((prev, curr) => curr.startBeat > prev.startBeat ? curr : prev)
      : null;

    const nextBeat = lastChord ? lastChord.startBeat + lastChord.duration : 0;
    onAddChord(scaleDegree, { x: nextBeat * beatWidth });
  }, [chords, beatWidth, onAddChord]);

  // Create a mock chord for the drag overlay when dragging from palette
  const palettePreviewChord: Chord | null = activePaletteItem ? {
    id: 'palette-preview',
    scaleDegree: activePaletteItem.scaleDegree,
    quality: 'major',
    extensions: {},
    key: currentKey,
    mode: currentMode,
    isChromatic: false,
    voices: { soprano: '', alto: '', tenor: '', bass: '' },
    startBeat: 0,
    duration: 1,
    position: { x: 0, y: 0 },
    size: 60,
    selected: false,
    playing: false,
    source: 'user',
    createdAt: new Date().toISOString(),
  } : null;

  // Only restrict to horizontal axis when dragging existing chords, not palette items or suspensions
  const modifiers = (activePaletteItem || activeSuspensionType) ? [] : [restrictToHorizontalAxis];

  return (
    <DndContext
      sensors={sensors}
      modifiers={modifiers}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragCancel={handleDragCancel}
    >
      <div
        ref={canvasContainerRef}
        className={styles.canvasWrapper}
        onClick={handleCanvasClick}
        onMouseDown={handleSelectionStart}
        onMouseMove={handleSelectionMove}
        onMouseUp={handleSelectionEnd}
        onMouseLeave={handleSelectionEnd}
        onDragOver={handleSnippetDragOver}
        onDrop={handleSnippetDrop}
      >
        <Canvas
          ref={canvasScrollRef}
          currentKey={currentKey}
          currentMode={currentMode}
          beatsPerMeasure={beatsPerMeasure}
          zoom={zoom}
          isPlaying={isPlaying}
          playheadPosition={playheadPosition}
          totalBeats={totalBeats}
          phrases={phrases}
          showVoiceLanes={showVoiceLanes}
          chords={chords}
          onAddChord={onAddChord}
          onZoomChange={onZoomChange}
          onAnalyze={onAnalyze}
          onRefine={onRefine}
          onDeepInsight={onDeepInsight}
          hasChords={chords.length > 0}
          hasSelection={selectedChordIds.length > 0}
          onSeek={onSeek}
        >
          {chords.map(chord => (
            <DraggableChord
              key={chord.id}
              chord={chord}
              allChords={chords}
              songContext={songContext}
              isSelected={selectedChordIds.includes(chord.id)}
              isPlaying={isPlaying && chord.playing}
              onClick={(e) => handleChordClick(chord, e)}
              onUpdateChord={onUpdateChord}
              onPlayChord={onPlayChord}
              zoom={zoom}
              showVoiceLanes={showVoiceLanes}
            />
          ))}

          {/* Drop zones for suspensions (GRE-113) */}
          {dropZones.map((zone) => (
            <ChordDropZone
              key={zone.id}
              id={zone.id}
              sourceChordId={zone.sourceChordId}
              targetChordId={zone.targetChordId}
              sourcePosition={getChordPosition(zone.sourceChordId)}
              targetPosition={getChordPosition(zone.targetChordId)}
              isActive={isDraggingSuspension || !!activeSuspensionType}
              isOver={hoveredDropZoneId === zone.id}
              isValid={zone.isValid}
              invalidReason={zone.invalidReason}
            />
          ))}

          {/* Rendered suspension connectors (GRE-113) */}
          {suspensionConnectors.map((connector) => (
            <SuspensionConnectorShape
              key={connector.id}
              connector={connector}
              sourcePosition={getChordPosition(connector.sourceChordId)}
              targetPosition={getChordPosition(connector.targetChordId)}
              isSelected={selectedConnectorId === connector.id}
              isPlaying={connector.playing}
              onClick={() => handleConnectorClick(connector.id)}
              onContextMenu={(e) => handleConnectorContextMenu(e, connector.id)}
            />
          ))}

          {/* Natural Nexts - Chord Suggestions (GRE-119) */}
          {!showVoiceLanes && chords.length > 0 && onAuditionGhost && onCommitGhost && (
            <SuggestionsLayer
              chords={chords}
              musicKey={currentKey}
              mode={currentMode}
              zoom={zoom}
              onAuditionGhost={onAuditionGhost}
              onCommitGhost={onCommitGhost}
            />
          )}
        </Canvas>

        {/* Selection status — a line of handwriting, not a badge (foundation §6). */}
        {selectedChordIds.length > 1 && (
          <div className={styles.selectionInfo}>
            {describeSelectionCount(selectedChordIds.length)} chords selected
          </div>
        )}

        {/* Selection Box */}
        {selectionBox?.isSelecting && (
          <SelectionBox
            start={selectionBox.start}
            current={selectionBox.current}
          />
        )}

        {/* Chord Palette */}
        <ChordPalette
          mode={currentMode}
          onAddChord={handlePaletteAddChord}
          onBuildFromBones={onBuildFromBones}
          hasChords={chords.length > 0}
        />

        {/* Suspension Palette (GRE-113) */}
        <SuspensionPalette
          isVisible={suspensionPaletteVisible}
          onToggle={() => setSuspensionPaletteVisible(!suspensionPaletteVisible)}
        />

        {/* Suspension Context Menu (GRE-113) */}
        <SuspensionContextMenu
          isOpen={suspensionContextMenu.isOpen}
          position={suspensionContextMenu.position}
          connector={
            suspensionContextMenu.connectorId
              ? suspensionConnectors.find((c) => c.id === suspensionContextMenu.connectorId) || null
              : null
          }
          onClose={closeSuspensionContextMenu}
          onDelete={handleSuspensionDelete}
          onChangeType={handleSuspensionTypeChange}
          onChangeDuration={handleSuspensionDurationChange}
        />
      </div>

      {/* Drag Overlay */}
      <DragOverlay
        dropAnimation={{
          duration: 200,
          easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
        }}
      >
        {activeChord ? (
          <ChordShape
            chord={activeChord}
            zoom={zoom}
            isDragging
          />
        ) : palettePreviewChord ? (
          <ChordShape
            chord={palettePreviewChord}
            zoom={zoom}
            isDragging
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
